<template>
  <div id="brush-chart">
    <ChartWrapper
      :data="data"
      :width="width"
      :height="height"
      :chartDefaults="chartDefaults"
      :chartKeys="benchmarksChartKeys"
    >
      <template #bars="props">
        <GroupedBars
          v-bind="props"
          :barWidth="barWidth"
          :barPadding="barPadding"
          :highlightedArea="dataWindow"
          :barColors="benchmarksColors"
          :chartKeys="benchmarksChartKeys"
        />
      </template>
      <template v-slot:brush>
        <g ref="brush" class="brush"></g>
      </template>
    </ChartWrapper>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { scaleBand } from 'd3-scale';
import { brushX, select, selectAll, bisectRight } from 'd3';
import ChartWrapper from '../../Common/Charting/Shared/Graph/ChartWrapper.vue';
import GroupedBars from '../../Common/Charting/Shared/Graph/Bars/GroupedBars.vue';

export default {
  props: {
    data: {
      type: Array,
      required: true,
    },
    width: {
      type: Number,
      default: 500,
    },
    height: {
      type: Number,
      default: 93,
    },
    displayAmount: {
      type: Number,
      default: 11,
    },
  },
  components: {
    ChartWrapper,
    GroupedBars,
  },
  data() {
    return {
      barWidth: 5,
      barPadding: 1,
      dataWindow: [0, this.displayAmount + 1],
    };
  },
  computed: {
    ...mapGetters([
      'benchmarksColors',
      'benchmarksChartKeys',
    ]),
    chartDefaults() {
      return {
        dataWidth: this.barWidth * 2 + this.barPadding,
        xLabelPadding: 0,
        yLabelPadding: 0,
        yLabelWidth: 0,
        fontSize: 0,
        margin: {
          top: 10,
          right: 0,
          bottom: 0,
          left: 0,
        },
      };
    },
    brush() {
      return brushX(this.xScale)
        .extent([[0, 0], [this.width, this.height]])
        .on('brush', this.brushed);
    },
    brushWidth() {
      const selectionPercent = this.displayAmount / this.data.length;

      return Math.floor((selectionPercent * this.width) + ((this.barWidth + this.barPadding)/2));
    },
    xScale() {
      return scaleBand()
        .range([0, this.width])
        .paddingOuter(.3)
        .paddingInner(.6)
        .domain(this.data.map(d => d['dimension']));
    },
  },
  methods: {
    computeDataWindow(selection) {
      const range = this.xScale.domain().map(this.xScale);
      const i0 = bisectRight(range, selection[0]);
      const i1 = bisectRight(range, selection[1]);

      return i1 - i0 === this.displayAmount
        ? [i0, i1]
        : this.dataWindow;
    },
    brushed(event) {
      this.dataWindow = this.computeDataWindow(event.selection);
      this.$emit('select', this.dataWindow);
    },
    createBrush() {
      const brushNode = this.$refs.brush;

      select(brushNode)
        .call(this.brush)
        .call(this.brush.move, [0, this.brushWidth]);

      selectAll('.brush>.handle').remove();
      selectAll('.brush>.overlay').remove();
    },
  },
  mounted() {
    this.createBrush();
  },
  watch: {
    brushWidth() {
      this.createBrush();
    },
  },
};
</script>

<style>
.brush rect.selection {
  fill: black;
  opacity: .5;
}
</style>

<style scoped>
#brush-chart {
  font-family: Lato;
  font-weight: 500;
  height: 93px;
  border: .84px solid #C1CCD4;
}
</style>
